import { isSameStringIgnoreCase } from './isSelf';
import { getStore } from 'owa-mail-store';
import { isValidSmtpAddress } from 'owa-recipient-email-address/lib/utils/isValidRecipientAddress';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import ItemId from 'owa-service/lib/contract/ItemId';
import Message from 'owa-service/lib/contract/Message';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

/**
 * Gets alias addresses, i.e. addresses consumers have created to send
 * from that are different from their primary email addresses
 * @param primaryAddress, the user's primary email address
 */
function getAliasAddresses(primaryAddress: string): string[] {
    const sessionSettings = getUserConfiguration().SessionSettings;
    const aliasAddress = [];
    let addresses = sessionSettings.UserProxyAddresses;
    if (sessionSettings.NotManagedEmailAddresses) {
        addresses = addresses.concat(sessionSettings.NotManagedEmailAddresses);
    }

    if (addresses && addresses.length > 0) {
        addresses.forEach(address => {
            if (primaryAddress != address && !shouldSkipEmailAddress(address)) {
                aliasAddress.push(address);
            }
        });
    }

    return aliasAddress;
}

/**
 * Gets connected accounts, i.e. external email addresses users have connected to their accounts
 * @param currentList, the user's current email addresses
 */
function getConnectedAccounts(currentList: string[]): string[] {
    const connectedAccountInfos = getUserConfiguration().SessionSettings.ConnectedAccountInfos;

    if (connectedAccountInfos && connectedAccountInfos.length > 0) {
        connectedAccountInfos.forEach(connectedAccount => {
            if (currentList.lastIndexOf(connectedAccount.EmailAddress) == -1) {
                currentList.push(connectedAccount.EmailAddress);
            }
        });
    }

    return currentList;
}

/**
 * Gets the alternative from address, i.e. when a user has more than one address to send from
 */
// TODO: Parse through SharingInstanceGuid to determine what connected account info to use (VSO:29460)
export function getAlternativeFromAddress(): string {
    const defaultFromAddress = getDefaultAddress();
    const primaryAddress = getPrimaryAddress();
    if (primaryAddress == defaultFromAddress && shouldSkipEmailAddress(defaultFromAddress)) {
        const aliases = getAliasAddresses(primaryAddress);
        if (aliases && aliases.length > 0) {
            return aliases[0];
        }
    }

    return defaultFromAddress;
}

/**
 * Gets the address the email was sent to (when replying to an email)
 * Consumers can set the default address they send from, so this may be different
 * than the account name they used when initally creating the account
 * @param referenceItemId, the id for referencing the message
 * @returns the address sent to if it was an alias or connected account, otherwise null
 * (later logic treats null as the fallback to the default address)
 */
export function getSentToAddress(referenceItemId: ItemId): string {
    const mailStore = getStore();
    const items = mailStore && mailStore.items;
    let referenceItem: Message;
    if (items && referenceItemId && items.has(referenceItemId.Id)) {
        referenceItem = items.get(referenceItemId.Id) as Message;
    }

    if (referenceItem) {
        const recipientInFromAddresses = getRecipientInFromAddresses(
            referenceItem.ToRecipients,
            referenceItem.CcRecipients
        );
        if (recipientInFromAddresses && recipientInFromAddresses !== getDefaultAddress()) {
            return recipientInFromAddresses;
        }
    }
    return null;
}

export function getRecipientInFromAddresses(
    toRecipients: EmailAddressWrapper[],
    ccRecipients: EmailAddressWrapper[]
): string {
    const recipientInFromAddresses: string = tryFindRecipient(
        toRecipients,
        ccRecipients,
        getFromAddresses()
    );
    return recipientInFromAddresses || getDefaultAddress();
}

/*
 * Get the default email address to send from
 * Since consumers can set the default address they send from, this may be different
 * than the address they used when initially creating the account
 */
export function getDefaultAddress(): string {
    const userConfig = getUserConfiguration();
    const address = isConsumer()
        ? userConfig.SessionSettings.DefaultFromAddress
        : userConfig.UserOptions.SendAddressDefault;

    // Verify that the address is a valid SMTP address
    // If not, e.g. it is the GUID, fall back to the primary address (VSO 45276)
    return isValidSmtpAddress(address) ? address : getPrimaryAddress();
}

/**
 * Get the primary email address, i.e. the address used to initially create the account
 */
function getPrimaryAddress(): string {
    return getUserConfiguration().SessionSettings.UserEmailAddress;
}

/**
 * Get the from address that should be shown in the From well
 * @param referenceItemId, the id for referencing the message
 */
export function getFromAddress(referenceItemId?: ItemId): EmailAddressWrapper {
    let fromAddress: string = null;

    const sentToAddress = getSentToAddress(referenceItemId);
    if (sentToAddress) {
        fromAddress = sentToAddress;
    } else {
        fromAddress = getAlternativeFromAddress();
    }

    return <EmailAddressWrapper>{
        MailboxType: 'Mailbox',
        RoutingType: 'SMTP',
        EmailAddress: fromAddress,
        Name: getUserConfiguration().SessionSettings.UserDisplayName,
    };
}

/**
 * Gets all addresses a user can send from
 * This includes the default email address, alias addresses, and connected account addresses
 */
export function getFromAddresses(): string[] {
    let sendAsAddresses: string[] = [];

    // Add the primary account address
    const primaryAddress = getPrimaryAddress();
    if (!shouldSkipEmailAddress(primaryAddress)) {
        sendAsAddresses.push(primaryAddress);
    }

    // Add alias for consumer users
    if (isConsumer()) {
        sendAsAddresses = sendAsAddresses.concat(getAliasAddresses(primaryAddress));
    }

    // Add connected accounts
    sendAsAddresses = getConnectedAccounts(sendAsAddresses);

    return sendAsAddresses;
}

// Helper functions for finding From addresses

export function shouldSkipEmailAddress(emailAddress: string): boolean {
    let shouldSkip = false;
    if (isConsumer()) {
        const userConfig = getUserConfiguration();
        emailAddress = emailAddress.toLowerCase();
        shouldSkip =
            emailAddress.indexOf('outlook_' + userConfig.HexCID) == 0 ||
            emailAddress.indexOf('@shadow.outlook.com') != -1;
    }

    return shouldSkip;
}

function isInRecipients(emailAddress: string, recipients: EmailAddressWrapper[]): boolean {
    if (recipients && recipients.length > 0) {
        for (let recipient of recipients) {
            if (isSameStringIgnoreCase(emailAddress, recipient.EmailAddress)) {
                return true;
            }
        }
    }
    return false;
}

function tryFindRecipient(
    toRecipients: EmailAddressWrapper[],
    ccRecipients: EmailAddressWrapper[],
    addresses: string[]
): string {
    for (let i = 0; i < addresses.length; i++) {
        if (
            isInRecipients(addresses[i], toRecipients) ||
            isInRecipients(addresses[i], ccRecipients)
        ) {
            return addresses[i];
        }
    }
    return null;
}
